import {
  SALES_SITE_ID_MAX_LENGTH,
  SALES_SITE_OTHER_MAX_LENGTH,
  SecLoansHousingSalesSite,
  SecLoansLotOwnership,
} from '@shared/sec-loans'
import { DateInputField, DateInputFieldProps } from '@ui-common/components/DateInputField'
import { OtherTextInputField } from '@ui-common/components/OtherTextInput'
import { OTHER_VALUE } from '@ui-common/constants'
import { YesNo } from '@ui-common/types'
import { buildOptionList, commonYesNoOptions } from '@ui-common/utils/form'
import { FormRadioGroup } from '@ui-components/form/FormRadioGroup'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { FormGroupProps, FormTextInputProps } from '@ui-components/global/ComponentProps'
import { PropsWithChildren, ReactNode, RefAttributes } from 'react'
import { FieldError, FieldErrors, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { SECOND_APPLICANT_PREFIX } from '../../constants/constants'
import { getSecLoansLocalizedEnums } from '../../utils/getSecLoansLocalizedEnums'
import { getResidenceInfoLabels } from '../steps/loan-target-phase/residence-info-step/residenceInfoStepUtils'
import { ResidenceDataTypes } from '../steps/loan-target-phase/residence-info-step/types'
import { ApplicantSticker, ApplicantStickerProps } from './ApplicantSticker'

const getError = (errors: FieldErrors, name: string): FieldError => {
  if (name.startsWith(SECOND_APPLICANT_PREFIX)) {
    const [secondApplicantKey, field] = name.split('.')
    if (secondApplicantKey && field) {
      const fieldErrors = errors[secondApplicantKey] as FieldErrors
      return fieldErrors?.[field] as FieldError
    }
  }
  return errors[name] as FieldError
}

interface StickerProps {
  sticker?: ApplicantStickerProps
}

type StickeredRadioGroupProps = PropsWithChildren<FormGroupProps & StickerProps>

export const StickerWrapper = (props: PropsWithChildren<StickerProps>) => {
  const { sticker, children } = props
  return (
    <div>
      {sticker && <ApplicantSticker {...sticker} />}
      {children}
    </div>
  )
}

export const RadioGroup = (props: StickeredRadioGroupProps): ReactNode => {
  const { sticker, children, ...rest } = props
  return (
    <StickerWrapper sticker={sticker}>
      <FormRadioGroup {...rest}>{children}</FormRadioGroup>
    </StickerWrapper>
  )
}

type StickeredTextInputProps = PropsWithChildren<FormTextInputProps & StickerProps & RefAttributes<HTMLInputElement>>

export const TextInput = (props: StickeredTextInputProps): ReactNode => {
  const { sticker, children, ...rest } = props
  return (
    <StickerWrapper sticker={sticker}>
      <FormTextInput {...rest}>{children}</FormTextInput>
    </StickerWrapper>
  )
}

type StickeredInputProps = PropsWithChildren<StickerProps & RefAttributes<HTMLInputElement>>

export const LotTypeSelector = (props: StickeredInputProps & NameProp): ReactNode => {
  const { sticker, children, name, ref, ...rest } = props

  const intl = useIntl()
  const label = getResidenceInfoLabels(intl)
  const { lotOwnerShipTexts } = getSecLoansLocalizedEnums(intl.formatMessage)
  const methods = useFormContext()

  const { formState } = methods
  const { errors } = formState

  return (
    <StickerWrapper sticker={sticker}>
      <FormRadioGroup
        name={name}
        ref={ref}
        label={label[ResidenceDataTypes.LotType]}
        options={buildOptionList(Object.values(SecLoansLotOwnership), lotOwnerShipTexts)}
        required={true}
        error={getError(errors, name)?.message?.toString()}
        {...rest}
      >
        {children}
      </FormRadioGroup>
    </StickerWrapper>
  )
}

interface NameProp {
  name: string
}

interface OtherLabelProp {
  otherLabel?: string
}

export const SalesSiteSelector = (props: StickeredInputProps & NameProp & OtherLabelProp) => {
  const { sticker, children, name, otherLabel, ...rest } = props

  const intl = useIntl()
  const label = getResidenceInfoLabels(intl)

  const { salesSiteTexts } = getSecLoansLocalizedEnums(intl.formatMessage)
  const methods = useFormContext()

  const { register, formState, watch } = methods
  const { errors } = formState

  const valueWatch = watch(name)
  const hasSelectedOtherValue = valueWatch === SecLoansHousingSalesSite.Other
  const hasSalesSite = watch('hasSalesSite')
  const otherSiteName = `${name}Other`
  const siteTargetIdName = `${name}TargetId`
  return (
    <StickerWrapper sticker={sticker}>
      <FormRadioGroup
        options={commonYesNoOptions(intl).reverse()}
        label={label[ResidenceDataTypes.HasSalesSite]}
        {...register('hasSalesSite')}
      >
        {hasSalesSite === YesNo.Yes && (
          <FormRadioGroup
            label={label[ResidenceDataTypes.SalesSite]}
            options={buildOptionList(Object.values(SecLoansHousingSalesSite), salesSiteTexts)}
            error={getError(errors, name)?.message?.toString()}
            required={true}
            {...register(name, { shouldUnregister: true })}
            {...rest}
          >
            {valueWatch && !hasSelectedOtherValue && (
              <TextInput
                maxLength={SALES_SITE_ID_MAX_LENGTH}
                error={getError(errors, siteTargetIdName)?.message?.toString()}
                required={true}
                label={label[ResidenceDataTypes.SalesSiteId]}
                {...register(siteTargetIdName, { shouldUnregister: true })}
              />
            )}
            {valueWatch && hasSelectedOtherValue && (
              <OtherTextInputField
                label={otherLabel}
                maxLength={SALES_SITE_OTHER_MAX_LENGTH}
                fieldError={getError(errors, otherSiteName)}
                formFieldRef={register(otherSiteName, { shouldUnregister: true })}
                description={intl.formatMessage({ id: 'sec-loans-housing-sales-site-other-description' })}
              />
            )}
            {children}
          </FormRadioGroup>
        )}
      </FormRadioGroup>
    </StickerWrapper>
  )
}

export const NumberRadioGroup = (
  props: FormGroupProps & {
    sticker?: ApplicantStickerProps
    otherLabel?: string
  },
): ReactNode => {
  const { name, otherLabel } = props

  const methods = useFormContext()

  const { register, formState, watch } = methods
  const { errors } = formState

  const valueWatch = watch(name)

  const otherFieldName = `${name}Other`

  return (
    <RadioGroup {...props} error={getError(errors, name)?.message?.toString()}>
      {valueWatch === OTHER_VALUE && (
        <OtherTextInputField
          label={otherLabel}
          inputMode="numeric"
          fieldError={getError(errors, otherFieldName)}
          formFieldRef={register(otherFieldName, { shouldUnregister: true })}
        />
      )}
      {props.children}
    </RadioGroup>
  )
}

type DateInputFieldWithStickerProps<T extends object> = Omit<StickeredTextInputProps, 'name'> & DateInputFieldProps<T>

export const DateInputFieldWithSticker = <T extends object>(props: DateInputFieldWithStickerProps<T>): ReactNode => {
  const { children, sticker, ...rest } = props

  return (
    <StickerWrapper sticker={sticker}>
      <DateInputField<T> {...rest}>{children}</DateInputField>
    </StickerWrapper>
  )
}
